<template>
  <div class="filter-list-container">
    <FilterShrink class="aside-filter-container left-container">
      <template #default>
        <div class="container-wrapper">
          <div class="chart-container">
            <slot name="chart"></slot>
          </div>
          <div v-if="isTalentPool && !isPotential && !hiddenReportChat" class="talent-filter-group-content">
            <div id="talent-filter-group-name" class="talent-filter-group-name">{{ currentGroupFilter.group_name }}</div>
              <div class="talent-filter-group-chart">
                <div ref="talentFilterGroupReport" class="chart-score talent-filter-report"></div>
                <div id="talent-filter-group-count" class="talent-filter-group-count">{{ groupReportTotalCount|formatBigNumber }}</div>
            </div>
          </div>
          <div v-if="showAISearch && hasAISearchPermission" class="ai-search-container" :class="disableAISearch && 'is-disabled'">
            <div class="container-label">
              <label>{{ $t(`${i18nPrefix}.label.aiSearch`) }}</label>
            </div>
            <div class="container-content">
              <div class="ai-search-content">
                <el-input v-model="aiSearch" type="textarea" resize="none" :placeholder="$t(`${i18nPrefix}.placeholder.aiSearch`)" :disabled="disableAISearch" :autosize="{ minRows: 2, maxRows: 6 }" @keyup.enter.native="handleAISearch"></el-input>
              </div>
              <div class="ai-search-footer">
                <i class="search-icon iconfont icona-AISearchButton-09" @click="handleAISearch"></i>
              </div>
            </div>
          </div>
          <div v-if="!hiddenKeywordSearch" class="keywords-filter-container" :class="(!hiddenCandidateNameSearch || !hiddenCandidateIdSearch) && 'has-candidate-filter'">
            <el-select
              v-model="search.keywords"
              clearable
              multiple
              remote
              filterable
              allow-create
              default-first-option
              ref="keywordsSelect"
              class="keywords-filter"
              prefix-icon="el-icon-search"
              :placeholder="$t(`${i18nPrefix}.placeholder.keywordsSearch`)"
              @paste.native="handleSearchSelectPaste($event, 'keywords')"
            ></el-select>
          </div>
          <div v-if="!hiddenCandidateNameSearch" class="candidate-name-filter-container" :class="(!hiddenCandidateIdSearch || !hiddenMyCandidateSwitch) && 'has-candidate-id-filter'">
            <el-input
              v-model="search.candidateName"
              clearable
              class="candidate-name-filter"
              :placeholder="$t(`${i18nPrefix}.placeholder.candidateNameSearch`)"
            ></el-input>
          </div>
          <div v-if="!hiddenCandidateIdSearch" class="candidate-id-filter-container" :class="(!hiddenEmployeeIdSearch || !hiddenMyCandidateSwitch) && 'has-employee-id-filter'">
            <custom-remote-el-select
              v-model="search.candidateId"
              multiple
              clearable
              allowCreate
              filterable
              remote
              defaultFirstOption
              class="candidate-id-filter"
              :placeholder="$t(`${i18nPrefix}.placeholder.candidateIdSearch`)"
              @paste.native="handleSearchSelectPaste($event, 'candidateId')"
              @change="handleCandidateIdChange"
              @clear="handleCandidateIdClear"
            ></custom-remote-el-select>
          </div>
          <div v-if="!hiddenEmployeeIdSearch" class="employee-id-filter-container" :class="!hiddenMyCandidateSwitch && 'has-my-candidate-switch'">
            <custom-remote-el-select
              v-model="search.employeeId"
              multiple
              clearable
              allowCreate
              filterable
              remote
              defaultFirstOption
              class="employee-id-filter"
              :placeholder="$t(`${i18nPrefix}.placeholder.employeeIdSearch`)"
              @paste.native="handleSearchSelectPaste($event, 'employeeId')"
              @change="handleEmployeeIdChange"
              @clear="handleEmployeeIdClear"
            ></custom-remote-el-select>
          </div>
          <div v-if="!hiddenMyCandidateSwitch" class="my-candidate-switch-container">
            <el-switch v-model="onlyMyCandidate" class="my-candidate" :inactive-text="$t(`${i18nPrefix}.label.myCandidates`)" @change="handleOnlyMyCandidateChange"></el-switch>
          </div>
          <div class="search-template-container">
            <div class="container-label">
              <label>{{ $t(`${i18nPrefix}.label.saveSearch`) }}</label>
            </div>
            <div class="container-content">
              <el-select
                clearable
                filterable
                value-key="name"
                popper-class="search-template-dropdown"
                :value="searchTemplate"
                @change="onSearchTemplateChange"
              >
                <el-option
                  v-for="(item, index) in searchTemplateList"
                  :key="index"
                  :label="item.name"
                  :value="item"
                >
                  <div class="option">
                    <span class="label">{{ item.name }}</span>
                    <i class="delete-icon el-icon-delete" @click.stop="deleteSearchTemplate(item, index)"></i>
                  </div>
                </el-option>
              </el-select>
            </div>
          </div>
          <div class="filter-group-list">
            <FilterGroup
              v-for="(group, groupKey) in wrappedFilterGroupList"
              v-model="search[groupKey]"
              class="filter-group"
              ref="filterGroup"
              :key="groupKey"
              :class="group.class"
              :defaultExpand="group.defaultExpand"
              :title="group.title"
              :filterList="group.filterList"
              @expandChange="expandChange($event, groupKey)"
            >
              <template v-for="filter in group.filterList" #[filter.labelSlot]="item">
                <slot :name="filter.labelSlot" :filter="item.filter" :filterName="item.filterName" :search="search[groupKey]"></slot>
              </template>
              <template v-for="filter in group.filterList" #[filter.extraSlot]="item">
                <slot :name="filter.extraSlot" :filter="item.filter" :filterName="item.filterName" :search="search[groupKey]"></slot>
              </template>
            </FilterGroup>
          </div>
        </div>
      </template>
    </FilterShrink>
    <div class="main-container" :class="[showAISearchCollapse && 'ai-collapse', showCollapse && 'is-show']">
      <div class="container-content">
        <div class="content-wrapper">
          <slot name="topContainer">
          </slot>
          <div class="list-container">
            <div v-if="jobName" class="job-name">{{ jobName }}</div>
            <div ref="listHead" class="list-head">
              <el-checkbox v-if="listType === 'card'" class="select-all" :indeterminate="isIndeterminate" v-model="checkAllStatus" @change="handelSelectAll">{{ $t('common.label.selectAll') }}</el-checkbox>
              <div class="search-param-container">
                <div class="search-param-list-wrapper">
                  <div class="search-param-list">
                    <slot name="selectCountContainer">
                      <div class="candidate-count-container">
                    <span class="candidate-count total-count">
                      {{ candidateCount }}
                    </span>
                        <span v-if="!hiddenCurrentSelectedCount" class="candidate-count selected-count">
                      {{ `${$t(`${i18nPrefix}.label.selected`)} ${formatBigNumber(currentSelectedCount)}` }}
                    </span>
                      </div>
                    </slot>
                    <el-tooltip
                      v-for="(param, index) in searchDisplayValues"
                      effect="light"
                      popper-class="light-tooltip-popper search-param-tooltip"
                      :key="index"
                    >
                      <div class="search-param" :class="param.className">
                        <span class="param-summary">{{ param.summary }}</span>
                        <div v-if="showSearchValueCancelBtn(param)" class="cancel-btn" @click="onSearchValueCancel(param, index)">
                          <i class="btn-icon el-icon-close"></i>
                        </div>
                      </div>
                      <template #content>
                        <div class="param-detail">{{ param.detail }}</div>
                      </template>
                    </el-tooltip>
                  </div>
                </div>
                <div v-show="searchDisplayValues && searchDisplayValues.length > 0" class="operate-btn-group">
                  <el-tooltip
                    effect="light"
                    popper-class="light-tooltip-popper save-btn-tooltip"
                    :content="$t(`common.btn.save`)"
                  >
                    <div class="operate-btn save-btn" @click="handleSearchSaveBtnClick">
                      <i class="btn-icon iconfont icona-SaveAs"></i>
                    </div>
                  </el-tooltip>
                  <el-tooltip
                    effect="light"
                    popper-class="light-tooltip-popper reset-btn-tooltip"
                    :content="$t(`common.btn.clear`)"
                  >
                    <div class="operate-btn reset-btn" @click="handleSearchResetBtnClick">
                      <i class="btn-icon iconfont iconRefresh"></i>
                    </div>
                  </el-tooltip>
                </div>
              </div>
              <slot name="headBtnContainer">
                <div class="head-btn-group">
                  <el-dropdown
                    v-if="aiAnalysisActionList.length && !isPotential"
                    class="head-btn action-dropdown"
                    trigger="click"
                    :disabled="noAction"
                    @command="handleAction"
                  >
                    <el-button v-if="tooltip" v-auto-tooltip.always="tooltip" class="dropdown-btn" :disabled="noAction">
                      <span>{{ $t(`${i18nPrefix}.label.aiAnalysisAction`) }}</span>
                      <i class="ai-iconfont iconfont icona-ResumeSummary"></i>
                    </el-button>
                    <el-button v-else class="dropdown-btn" :disabled="noAction">
                      <span>{{ $t(`${i18nPrefix}.label.aiAnalysisAction`) }}</span>
                      <i class="ai-iconfont iconfont icona-ResumeSummary"></i>
                    </el-button>
                    <template #dropdown>
                      <el-dropdown-menu class="no-arrow-dropdown-menu">
                        <div v-for="(item, index) in aiAnalysisActionList" :key="index">
                          <div v-if="item.disabled && (item.tip || item.tipKey)" v-auto-tooltip.always="item.tip || $t(`${item.tipKey}`)" class="disabled-dropdown-item">
                            <el-dropdown-item
                              :command="item"
                              :disabled="item.disabled"
                            >
                              <span v-if="!item.childrenMenuList">{{ item.label }}</span>
                              <el-popover
                                v-if="item.childrenMenuList"
                                v-model="item.visible"
                                placement="left"
                                trigger="hover"
                                popper-class="action-children-container"
                                :ref="`popover_${item.label}`"
                              >
                                <div
                                  v-for="(child, childIndex) in item.childrenMenuList"
                                  v-show="!child.hidden"
                                  class="child-item"
                                  :key="childIndex"
                                  @click="handleAction(child, item)"
                                >
                                  {{ child.label }}
                                </div>
                                <template #reference>
                                  <div>{{ item.label }}</div>
                                </template>
                              </el-popover>
                            </el-dropdown-item>
                          </div>
                          <el-dropdown-item v-else :class="item.disabled && 'disabled-dropdown-item'" :command="item" :disabled="item.disabled">
                            <span v-if="!item.childrenMenuList">{{ item.label }}</span>
                            <el-popover
                              v-if="item.childrenMenuList"
                              v-model="item.visible"
                              placement="left"
                              trigger="hover"
                              popper-class="action-children-container"
                              :ref="`popover_${item.label}`"
                            >
                              <div
                                v-for="(child, childIndex) in item.childrenMenuList"
                                v-show="!child.hidden"
                                class="child-item"
                                :key="childIndex"
                                @click="handleAction(child, item)"
                              >
                                {{ child.label }}
                              </div>
                              <template #reference>
                                <div>{{ item.label }}</div>
                              </template>
                            </el-popover>
                          </el-dropdown-item>
                        </div>
                      </el-dropdown-menu>
                    </template>
                  </el-dropdown>
                  <el-dropdown
                    v-if="!isPotential"
                    class="head-btn action-dropdown"
                    trigger="click"
                    :disabled="noAction"
                    @command="handleAction"
                  >
                    <el-button v-if="tooltip" v-auto-tooltip.always="tooltip" class="dropdown-btn" :disabled="noAction">
                      <span>{{ $t('common.label.operate') }}</span>
                      <i class="dropdown-btn-icon el-icon-more"></i>
                    </el-button>
                    <el-button v-else class="dropdown-btn" :disabled="noAction">
                      <span>{{ $t('common.label.operate') }}</span>
                      <i class="dropdown-btn-icon el-icon-more"></i>
                    </el-button>
                    <template #dropdown>
                      <el-dropdown-menu class="no-arrow-dropdown-menu">
                        <el-dropdown-item
                          v-for="(item, index) in actionList"
                          :key="index"
                          :command="item"
                        >
                          <span v-if="!item.childrenMenuList">{{ item.label }}</span>
                          <el-popover
                            v-if="item.childrenMenuList"
                            placement="left"
                            v-model="item.visible"
                            trigger="hover"
                            popper-class="action-children-container"
                            :ref="`popover_${item.label}`"
                          >
                            <div
                              v-for="(child, childIndex) in item.childrenMenuList"
                              v-show="!child.hidden"
                              class="child-item"
                              :key="childIndex"
                              @click="handleAction(child, item)"
                            >
                              {{ child.label }}
                            </div>
                            <div slot="reference">{{ item.label }}</div>
                          </el-popover>
                        </el-dropdown-item>
                      </el-dropdown-menu>
                    </template>
                  </el-dropdown>
                  <el-dropdown
                    v-if="addBtnList && addBtnList.length > 0"
                    class="head-btn action-dropdown"
                    trigger="click"
                    @command="handleAddBtnClick"
                    :disabled="noAction"
                  >
                    <el-button v-if="tooltip" v-auto-tooltip.always="tooltip" class="head-btn add-btn" :disabled="noAction">
                      <i class="btn-icon iconfont iconAdd"></i>
                    </el-button>
                    <el-button v-else class="head-btn add-btn" :disabled="noAction">
                      <i class="btn-icon iconfont iconAdd"></i>
                    </el-button>
                    <template #dropdown>
                      <el-dropdown-menu class="no-arrow-dropdown-menu">
                        <!-- <el-dropdown-item
                          v-for="(item, index) in addBtnList"
                          :key="index"
                          :command="item"
                        >{{ item.label }}</el-dropdown-item> -->
                        <el-dropdown-item
                          v-for="(item, index) in addBtnList"
                          :key="index"
                          :command="item"
                        >
                          <span v-if="!item.childrenMenuList">{{ item.label }}</span>
                          <el-popover
                            v-if="item.childrenMenuList"
                            placement="left"
                            v-model="item.visible"
                            trigger="hover"
                            popper-class="action-children-container"
                            :ref="`popover_${item.label}`"
                          >
                            <div
                              v-for="(child, childIndex) in item.childrenMenuList"
                              v-show="!child.hidden"
                              class="child-item"
                              :key="childIndex"
                              @click="handleAction(child, item)"
                            >
                              {{ child.label }}
                            </div>
                            <div slot="reference">{{ item.label }}</div>
                          </el-popover>
                        </el-dropdown-item>
                      </el-dropdown-menu>
                    </template>
                  </el-dropdown>
                  <el-tooltip
                    v-if="hasSoftUserPermission && !isAIRecommendedList"
                    effect="light"
                    popper-class="light-tooltip-popper reset-btn-tooltip"
                    :content="$t(`${i18nPrefix}.btn.${isPotential ? 'viewCandidate' : 'viewLeads'}`)"
                  >
                    <div class="head-btn potential-btn" @click="onPotentialBtnClick">
                      <i class="btn-icon iconfont" :class="isPotential ? 'icona-afterclick' : 'icona-Talentfunnel'"></i>
                    </div>
                  </el-tooltip>

                  <div v-if="!isPotential" class="head-btn list-type-button" @click="onListTypeBtnClick">
                    <i class="btn-icon iconfont" :class="listType === 'table' ? 'iconliebiao' : 'iconliebiaomoshibeifen' "></i>
                  </div>
                </div>
              </slot>
            </div>
            <div class="list-wrapper" :style="listWrapperStyle">
              <slot name="listContainer">
                <!-- <component
                  v-bind="candidateListComponent.props"
                  v-on="candidateListComponent.events"
                  ref="candidateListComponent"
                  :is="candidateListComponent.component"
                /> -->
              </slot>
              <slot v-if="listType === 'table'" name="tableContainer"></slot>
              <slot v-if="listType === 'card'" name="cardContainer"></slot>
              <RectLoading class="loading" v-show="listLoading"/>
            </div>
          </div>
        </div>
      </div>
      <AiSearchCollapseInfo
        v-if="showAISearchCollapse"
        ref="aiSearchCollapseInfo"
        class="collapse-container"
        :isTalentPool="isTalentPool"
        @open="handleOpen"
        @close="handleClose"
      />
    </div>
    <SaveSearchDialog
      ref="saveSearchDialog"
      :searchTemplateList="searchTemplateList"
      :searchTemplate="searchTemplate"
      @save="handleSaveSearchSave"
    >
    </SaveSearchDialog>
    <ConfirmDeleteDialog
      ref="confirmDeleteDialog"
      @save="handleConfirmDelete"
    >
    </ConfirmDeleteDialog>
  </div>
</template>

<script>
import _ from 'lodash'
import * as echarts from 'echarts'
import FilterGroup from './filterGroup.vue'
import SaveSearchDialog from '../saveSearchDialog.vue'
import ConfirmDeleteDialog from '../dialog/confirmDeleteDialog.vue'
// import CandidateList from './candidateList.vue'
// import JobCandidateList from '../../jobApplicantList/filterList/candidateList.vue'
// import TalentCandidateList from '../../talentPool/filterList/candidateList.vue'
import RectLoading from '@/components/loading/rectLoading'
import { formatBigNumber } from '@/components/filters'
import FilterShrink from '@/components/common/shrink/filterShrink.vue'
// import Card from '../../application/universalList/card.vue'
// import JobCard from '../../application/job/card.vue'
// import TalentCard from '../../application/talentList/card.vue'
import Clickoutside from 'element-ui/src/utils/clickoutside'
import { AI_EXTRA_PARAMS, AI_SEARCH_PARAMS_MAPPING, FILTER_GROUP_TYPE } from '@/constant/company'
import AiSearchCollapseInfo from '@/views/adminNew/candidate/profile/components/aiSearchCollapseInfo.vue'

const CHART_OPTION = {
  tooltip: {
    show: false,
    formatter: 'The report is in generating...'
  },
  dataset: {
  },
  angleAxis: {
    splitLine: {
      show: false
    },
    axisLine: {
      show: false
    },
    axisLabel: {
      show: false
    },
    axisTick: {
      show: false
    }
  },
  radiusAxis: {
    type: 'category',
    splitLine: {
      show: false
    },
    axisLine: {
      show: false
    },
    axisLabel: {
      show: false
    },
    axisTick: {
      show: false
    }
  },
  polar: {
    radius: ['30%', '80%']
  },
  series: [
    {
      type: 'bar',
      coordinateSystem: 'polar',
      roundCap: true,
      itemStyle: {
        color: '#ec7a3b'
      },
      stack: 'a'
    },
    {
      type: 'bar',
      coordinateSystem: 'polar',
      roundCap: true,
      itemStyle: {
        color: '#f1d45f'
      },
      stack: 'a'
    },
    {
      type: 'bar',
      coordinateSystem: 'polar',
      roundCap: true,
      itemStyle: {
        color: '#98cd95'
      },
      stack: 'a'
    },
    {
      type: 'bar',
      coordinateSystem: 'polar',
      roundCap: true,
      itemStyle: {
        color: '#b0dde2'
      },
      stack: 'a'
    },
    {
      type: 'bar',
      coordinateSystem: 'polar',
      roundCap: true,
      itemStyle: {
        color: '#6dadc8'
      },
      stack: 'a'
    }
  ],
  legend: {
  }
}

const NOT_GROUP_FILTER = ['candidateName', 'candidateId', 'employeeId']
const KEYWORDS = 'keywords'
const AI_SEARCH = 'aiSearch'

export default {
  directives: {
    Clickoutside
  },
  components: {
    AiSearchCollapseInfo: AiSearchCollapseInfo,
    FilterGroup,
    // CandidateList,
    SaveSearchDialog,
    ConfirmDeleteDialog,
    RectLoading,
    FilterShrink
  },
  props: {
    filterGroupList: {
      type: [Object, Array],
      required: true
    },
    actionList: {
      type: Array,
      required: false
    },
    addBtnList: {
      type: Array,
      required: false
    },
    searchTemplate: {
      required: true
    },
    searchTemplateList: {
      type: Array,
      default: () => []
    },
    candidateList: {
      type: Array,
      default: () => []
    },
    listLoading: {
      type: Boolean,
      default: false
    },
    operationList: {
      type: Array,
      required: false
    },
    candidateTotalCount: {
      required: true
    },
    currentSelectedCount: {
      required: false
    },
    isJobApplicantList: {
      type: Boolean,
      default: false
    },
    isTalentPool: {
      type: Boolean,
      default: false
    },
    currentGroupFilter: {
      type: Object,
      required: false
    },
    jobName: {
      default: ''
    },
    showAISearch: {
      type: Boolean,
      default: false
    },
    hiddenKeywordSearch: {
      type: Boolean,
      default: false
    },
    hiddenCandidateNameSearch: {
      type: Boolean,
      default: false
    },
    hiddenCandidateIdSearch: {
      type: Boolean,
      default: false
    },
    hiddenEmployeeIdSearch: {
      type: Boolean,
      default: false
    },
    hiddenMyCandidateSwitch: {
      type: Boolean,
      default: false
    },
    hiddenReportChat: {
      type: Boolean,
      default: false
    },
    isPotential: {
      type: Boolean,
      default: false
    },
    hiddenCurrentSelectedCount: {
      type: Boolean,
      default: false
    },
    hasSoftUserPermission: {
      type: Boolean,
      default: false
    },
    aiAnalysisActionList: {
      type: Array,
      default: () => []
    },
    isAIRecommendedList: {
      type: Boolean,
      default: false
    },
    noAction: {
      type: Boolean,
      default: false
    },
    tooltip: {
      type: String,
      default: null
    }
  },
  data () {
    const i18nPrefix = 'admin.application.applicantList'
    return {
      i18nPrefix,
      formatBigNumber,
      search: {},
      fetchKeyMap: {},
      wrappedFilterGroupList: [],
      wrappingFilterGroupList: true,
      doSearch: null,
      listWrapperStyle: null,
      isShowDeletePopover: false,
      selectedCount: 0,
      deletedTemplate: null,
      deletedTemplateIndex: null,
      listType: 'table',
      isShowMenu: false,
      countNameOrder: ['Hot', 'Active', 'Warm', 'Potential', 'Cold'],
      checkAllStatus: false,
      noTriggerSearch: false,
      onlyMyCandidate: false,
      showCollapse: false,
      aiSearch: null,
      aiSearchDisplayValue: null,
      aiSearchResult: null,
      disableAISearch: false
    }
  },
  computed: {
    fromJobPotential () {
      if (this.$route.params.jobId) {
        return true
      } else {
        return false
      }
    },
    isIndeterminate () {
      const hiddenCandidateList = _.filter(this.candidateList, candidate => candidate.is_agency_uploaded_and_hid)
      if (this.currentSelectedCount && ((this.currentSelectedCount + hiddenCandidateList.length) !== this.candidateTotalCount)) {
        return true
      }
      return false
    },
    candidateCount () {
      const label = this.$t(`${this.i18nPrefix}.label.${this.searchDisplayValues.length ? 'searchResult' : 'total'}`)
      return `${label} ${formatBigNumber(this.candidateTotalCount)}`
    },
    groupReportTotalCount () {
      return this.currentGroupFilter.report.total_count
    },
    hasAISearchPermission () {
      return this.hasAnyPermission(['PERM_AI_CHAT_SEARCH', 'PREMIUM_AI_CHAT_SEARCH'])
    },
    showAISearchCollapse () {
      return this.showAISearch && this.hasAISearchPermission
    },
    searchDisplayValues () {
      const displayValues = []
      if (this.aiSearchDisplayValue) {
        displayValues.push({
          groupKey: 'aiSearch',
          className: 'ai-search',
          value: this.aiSearchDisplayValue,
          summary: this.$t(`${this.i18nPrefix}.label.aiSearch`),
          detail: this.aiSearchDisplayValue
        })
      }
      _.forEach(this.search, (filterValues, groupKey) => {
        if (!filterValues || _.isEmpty(filterValues)) {
          return
        }
        if (groupKey === KEYWORDS && filterValues.length > 0) {
          _.forEach(filterValues, (val) => {
            displayValues.push({
              groupKey,
              value: val,
              summary: val,
              detail: `${this.$t(`${this.i18nPrefix}.label.keywords`)}: ${val}`
            })
          })
          return
        }
        if (groupKey === 'candidateName') {
          displayValues.push({
            groupKey,
            value: filterValues,
            summary: filterValues,
            detail: `${this.$t(`${this.i18nPrefix}.label.candidateName`)}: ${filterValues}`
          })
          return
        }
        if (groupKey === 'candidateId') {
          _.forEach(filterValues, (val) => {
            displayValues.push({
              groupKey,
              value: val,
              summary: val,
              detail: `${this.$t(`${this.i18nPrefix}.label.candidateId`)}: ${val}`
            })
          })
          return
        }
        if (groupKey === 'employeeId') {
          _.forEach(filterValues, (val) => {
            displayValues.push({
              groupKey,
              value: val,
              summary: val,
              detail: `${this.$t(`${this.i18nPrefix}.label.employeeId`)}: ${val}`
            })
          })
          return
        }
        const group = this.wrappedFilterGroupList[groupKey]
        if (!group) {
          return
        }
        const filterList = group.filterList
        _.forEach(filterValues, (val, filterName) => {
          const filter = filterList[filterName]
          const valueDisplay = filter?.valueDisplay
          if (!filter || group.hidden || filter?.hidden || valueDisplay?.hidden) {
            return
          }
          const getDisplay = valueDisplay?.formatter || this.defaultFilterValueDisplay
          const display = getDisplay(val, filter, filterName, group, groupKey, filterValues)
          if (!display) {
            return
          }
          if (['string', 'number'].includes(typeof display)) {
            displayValues.push({
              filter,
              filterName,
              group,
              groupKey,
              value: val,
              summary: display,
              detail: display
            })
          } else {
            let displayList = display
            if (!_.isArray(displayList)) {
              displayList = [display]
            }
            _.forEach(displayList, (displayItem) => {
              if (!displayItem.value || !displayItem.summary) {
                return
              }
              displayValues.push({
                filter,
                filterName,
                group,
                groupKey,
                value: displayItem.value,
                valueKey: displayItem.valueKey,
                summary: displayItem.summary,
                detail: displayItem.detail
              })
            })
          }
        })
      })
      return displayValues
    }
  },
  created () {
    this.awaitData = new Promise(resolve => {
      this.resolve = resolve
    })
  },
  mounted () {
    this.resolve()
    window.addEventListener('resize', this.refreshListWrapperStyle)
    this.refreshListWrapperStyle()
    if (this.showAISearchCollapse) {
      window.addEventListener('message', this.handleMessage)
    }
  },
  destroyed () {
    window.removeEventListener('resize', this.refreshListWrapperStyle)
    window.removeEventListener('message', this.handleMessage)
  },
  methods: {
    async refreshListWrapperStyle () {
      if (!this.$refs.listHead) {
        await this.$nextTick()
      }
      const el = this.$refs.listHead
      if (!el) {
        return
      }
      const parent = el.parentElement
      const style = getComputedStyle(parent)
      const rect = el.getBoundingClientRect()
      let height = parent.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom) - rect.height
      if (this.jobName) {
        const childRect = parent.firstElementChild.getBoundingClientRect()
        height -= (rect.top - childRect.top)
      }
      this.listWrapperStyle = {
        height: `${height}px`
      }
    },
    handelSelectAll (val) {
      this.$emit('selectAllCard', val)
    },
    handleAction (actionItem, action) {
      if (action.childrenMenuList) {
        const popoverRef = this.$refs[`popover_${action.label}`][0]
        popoverRef.doClose()
      }
      actionItem.handler && actionItem.handler()
    },
    async initTalentFilterReportChart () {
      await this.awaitData
      const groupReportChart = echarts.init(this.$refs.talentFilterGroupReport)
      let option
      const reportNameList = _.map(this.currentGroupFilter.report.items, 'name')
      this.countNameOrder = _.filter(this.countNameOrder, name => reportNameList.includes(name))
      _.forEach([this.currentGroupFilter], item => {
        const report = item.report
        report.items = _.map(this.countNameOrder, name => _.find(report.items, { name }))
      })
      const report = this.currentGroupFilter.report
      if (!report.items.find(item => item.count !== 0)) {
        option = this.getInProgressChartOption(report)
      } else {
        option = this.getDoneChartOption(report)
      }
      groupReportChart.setOption(option)
      this.initChartClickEvent(groupReportChart)
    },
    initChartClickEvent (groupReportChart) {
      groupReportChart.on('click', (params) => {
        const nameArray = params.name.split('_')
        const activeness = nameArray[0]
        this.searchTalentByActiveness(activeness)
      })
    },
    searchTalentByActiveness (activeness) {
      this.$emit('searchTalentByActiveness', activeness)
    },
    getInProgressChartOption (report) {
      const option = JSON.parse(JSON.stringify(CHART_OPTION))
      option.dataset = {
        source: _.map(report.items, (reportItem, index) => {
          const dataItem = _.map(this.currentGroupFilter.report.items, item => 0)
          if (reportItem.name === 'Cold') {
            dataItem[index] = 100
          }
          const name = reportItem.name
          return [
            name,
            ...dataItem
          ]
        })
      }
      option.angleAxis.max = 100
      option.series[option.series.length - 1].itemStyle.color = '#dcdfe6'
      option.tooltip.show = true
      option.tooltip.formatter = (params, ticket, callback) => {
        return this.$t('admin.crm.tooltip.talentFilterGroup.generatingReport')
      }

      return option
    },
    getDoneChartOption (report) {
      const option = JSON.parse(JSON.stringify(CHART_OPTION))
      let maxCount = report.total_count
      const emptyCount = _.map(this.countNameOrder, _.constant(0))
      option.dataset = {
        source: _.map(report.items, (item, index) => {
          const name = item.name
          const count = item.count
          if (count > maxCount) {
            maxCount = count
          }
          const data = [`${name}`, ...emptyCount]
          data[index + 1] = count
          return data
        })
      }
      if (!this.countNameOrder.includes('Potential')) {
        option.series.splice(3, 1)
      }
      option.angleAxis.max = maxCount
      return option
    },
    closeMenu () {
      this.isShowMenu = false
    },
    showOperateMenu () {
      this.isShowMenu = !this.isShowMenu
    },
    defineI18nProperty (src, dest, propertyName) {
      const vm = this
      Object.defineProperty(dest, propertyName, {
        get () {
          return src[propertyName] || vm.$t(src[`${propertyName}I18nKey`])
        }
      })
      return dest
    },
    wrapFilterGroupList () {
      this.wrappingFilterGroupList = true
      const groupList = {}
      _.forEach(this.filterGroupList, (group, groupKey) => {
        // 初始化时 filterGroupList可能因为异步获取hidden属性导致数组变化 使search中根据数组下标绑定的值异常 所以清空受影响的search
        // if (this.wrappedFilterGroupList[groupKey] && group.key !== this.wrappedFilterGroupList[groupKey]._group.key) {
        //   this.search[groupKey] = null
        // }
        if (group.hidden) {
          return
        }
        const filterList = {}
        const groupInfo = {
          _group: group,
          class: group.class,
          filterList: filterList,
          defaultExpand: group.defaultExpand
        }
        this.defineI18nProperty(group, groupInfo, 'title')
        _.forEach(group.filterList, (filter, filterName) => {
          if (filter.hidden) {
            return
          }
          let props = { clearable: true }
          if (filter.props) {
            _.forEach(props, (val, key) => {
              if (!_.has(filter.props, key)) {
                filter.props[key] = val
              }
            })
            props = filter.props
          }
          const events = {}
          const wrappedFilter = filterList[filterName] = {
            _filter: filter,
            elType: filter.elType,
            tip: filter.tip,
            class: filter.class,
            placeholder: filter.placeholder,
            valueDisplay: filter.valueDisplay,
            cancelSelected: filter.cancelSelected,
            notShow: filter.notShow,
            style: filter.style,
            loadTemplate: filter.loadTemplate,
            loadMore: filter.loadMore,
            initData: filter.initData,
            filterMethod: filter.filterMethod,
            extraSlot: filter.extraSlot,
            labelSlot: filter.labelSlot,
            enableExclude: filter.enableExclude,
            defaultExclude: filter.defaultExclude,
            labelContainer: filter.labelContainer,
            selectAllOption: filter.selectAll
              ? {
                  label: filter.selectAll.label || this.$t('common.label.selectAll'),
                  value: `__filter_select_all_${filterName}__`,
                  getOptions: filter.selectAll.getOptions
                }
              : null,
            isSelect: false,
            props,
            events
          }
          this.defineI18nProperty(filter, wrappedFilter, 'label')
          if (filter.multiple) {
            wrappedFilter.multiple = true
            if (typeof filter.elType === 'string' && !_.has(props, 'multiple')) {
              props.multiple = true
            }
          }
          if (filter.elType === 'el-select') {
            if (!filter.forceElSelect) {
              wrappedFilter.elType = 'custom-remote-el-select'
            }
            wrappedFilter.isSelect = true
            if (filter.optionValue) {
              wrappedFilter.valueKey = props.valueKey = filter.optionValue || 'value'
            }
            const option = wrappedFilter.option = {
              labelKey: filter.optionLabel || 'label',
              loading: false,
              hasLoad: true,
              hasSearch: false,
              loadedData: filter.optionList,
              data: filter.optionList,
              optionTooltip: filter.optionTooltip
            }
            if (filter.loader) {
              wrappedFilter.remoteLoadSelect = true
              props.remote = true
              option.loader = filter.loader
              const hasLoad = option.hasLoad = filter.optionList && filter.optionList.length > 0
              if (hasLoad) {
                option.loadedData = option.data = filter.optionList
              } else {
                option.loadedData = option.data = []
              }
            }
            if (filter.optionSearch) {
              props.filterable = true
              props.remote = true
              if (filter.optionSearch !== true) {
                const optionSearch = wrappedFilter.optionSearch = filter.optionSearch
                option.search = (keywords) => {
                  if (option.loading || !option.hasLoad) {
                    return
                  }
                  option.loading = true
                  option.hasFilter = true
                  Promise.resolve(optionSearch(keywords, option.loadedData)).then((result) => {
                    option.data = result
                  }).finally(() => {
                    option.loading = false
                  })
                }
              }
            }
            if (filter.multiple) {
              props.reserveKeyword = true
            }
            if (filter.optionGroup) {
              wrappedFilter.option.grouped = true
              wrappedFilter.option.groupOptionsDataKey = filter.optionGroup.dataKey || 'children'
              wrappedFilter.option.groupLabelKey = filter.optionGroup.labelKey || 'label'
            }
          }
          if (filter.props) {
            _.merge(filter.props, props)
          }
          if (filter.events) {
            _.merge(events, filter.events)
          }
        })
        if (Object.keys(groupInfo.filterList).length < 1) {
          return
        }
        groupList[groupKey] = groupInfo
      })
      this.wrappedFilterGroupList = groupList
      this.$nextTick(() => {
        this.wrappingFilterGroupList = false
      })
    },
    getFilterValueDisplay (value, filter, isExclude) {
      let display = value
      if (value && filter.isSelect && filter.valueKey) {
        display = value[filter.option.labelKey]
      }
      if (filter.enableExclude) {
        if (isExclude) {
          const excludeLabel = this.$t(`common.conditionType.${filter.multiple ? 'exclude' : 'notEqual'}`)
          display = `${excludeLabel} ${display}`
        } else {
          const includeLabel = this.$t(`common.conditionType.${filter.multiple ? 'include' : 'equal'}`)
          display = `${includeLabel} ${display}`
        }
      }
      return display
    },
    getFilterValue (value, filter) {
      if (filter.valueKey) {
        return value[filter.valueKey]
      } else {
        return value
      }
    },
    defaultFilterValueDisplay (value, filter, filterName, group, groupKey, filterValues) {
      const isExclude = filterValues?._exclude?.includes(filterName)
      if (filter.multiple) {
        return _.map(value, (valueItem, valueKey) => {
          const valueDisplay = this.getFilterValueDisplay(valueItem, filter, isExclude)
          return {
            valueKey,
            value: valueItem,
            detail: `${group.title} - ${filter.label}: ${valueDisplay}`,
            summary: `${valueDisplay}`
          }
        })
      } else {
        const valueDisplay = this.getFilterValueDisplay(value, filter, isExclude)
        return {
          value: value,
          detail: `${group.title} - ${filter.label}: ${valueDisplay}`,
          summary: `${valueDisplay}`
        }
      }
    },
    showSearchValueCancelBtn (param) {
      if (_.has(param.filter?.props, 'clearable')) {
        return param.filter.props.clearable
      }
      return true
    },
    onSearchValueCancel (param, paramIndex) {
      if (param.groupKey === AI_SEARCH) {
        _.forEach(this.search, (value, key) => {
          if (_.includes(AI_EXTRA_PARAMS, key)) {
            this.search[key] = null
          }
        })
        this.resetAiSearch()
        return
      }
      if (param.groupKey === KEYWORDS) {
        this.search[KEYWORDS] = null
        return
      }
      if (NOT_GROUP_FILTER.includes(param.groupKey)) {
        this.search[param.groupKey] = null
      }
      const filter = param.filter
      const filterName = param.filterName
      const groupKey = param.groupKey
      const filterValues = this.search[groupKey]
      const filterValue = filterValues[filterName]
      const value = param.value
      if (filter.cancelSelected) {
        filterValues[filterName] = filter.cancelSelected(filterValue, value, param.valueKey)
      } else {
        if (filter.multiple) {
          filterValues[filterName] = _.filter(filterValue, valueItem => valueItem !== value)
        } else {
          filterValues[filterName] = null
        }
      }
    },
    dumpSearch () {
      const copy = {}
      _.forEach(this.search, (filterValues, groupKey) => {
        if (_.includes(AI_EXTRA_PARAMS, groupKey)) {
          return
        }
        if (groupKey === KEYWORDS) {
          copy.keywords = _.join(filterValues, '|')
          return
        }
        if (NOT_GROUP_FILTER.includes(groupKey)) {
          copy[groupKey] = filterValues
          return
        }
        const group = this.wrappedFilterGroupList[groupKey]
        const filterList = group.filterList
        const copyValues = copy[groupKey] = {}
        _.forEach(filterValues, (value, filterName) => {
          if (filterName === '_exclude' || _.endsWith(filterName, '_isUseAiSearch')) {
            copyValues[filterName] = value
            return
          }
          if (this.isNullOrUndefinedOrEmpty(value)) {
            return
          }
          const filter = filterList[filterName]
          if (!filter) {
            return
          }
          if (filter.isSelect) {
            if (filter.multiple) {
              copyValues[filterName] = _.map(value, (valueItem) => this.getFilterValue(valueItem, filter))
            } else {
              copyValues[filterName] = [this.getFilterValue(value, filter)]
            }
          } else {
            copyValues[filterName] = value
          }
        })
      })
      return copy
    },
    formatSearch (search) {
      const copy = {}
      _.forEach(search, (filterValues, groupKey) => {
        if (!filterValues || filterValues.length === 0) {
          return
        }
        if (_.includes(AI_EXTRA_PARAMS, groupKey)) {
          copy[groupKey] = filterValues
          return
        }
        if (groupKey === KEYWORDS) {
          if (_.isArray(filterValues)) {
            copy.keywords = _.join(filterValues, '|')
          } else {
            copy.keywords = filterValues || null
          }
          return
        }
        if (NOT_GROUP_FILTER.includes(groupKey)) {
          copy[groupKey] = filterValues
          return
        }
        const group = this.wrappedFilterGroupList[groupKey]
        const filterList = group.filterList
        const copyValues = copy[groupKey] = {}
        _.forEach(filterValues, (value, filterName) => {
          if (filterName === '_exclude' || _.endsWith(filterName, '_isUseAiSearch')) {
            copyValues[filterName] = value
            return
          }
          if (this.isNullOrUndefinedOrEmpty(value)) {
            return
          }
          const filter = filterList[filterName]
          if (!filter) {
            return
          }
          if (filter.isSelect) {
            if (filter.multiple) {
              copyValues[filterName] = _.map(value, (valueItem) => this.getFilterValue(valueItem, filter))
            } else {
              copyValues[filterName] = [value.value]
            }
          } else {
            copyValues[filterName] = value
          }
        })
      })
      return copy
    },
    async resetSearch (template, noTriggerSearch) {
      this.noTriggerSearch = Boolean(noTriggerSearch)
      if (template) {
        const search = {}
        const promiseList = []
        const formattedSearch = {}
        _.forEach(template.search, (filterValues, groupKey) => {
          if (this.isNullOrUndefinedOrEmpty(filterValues)) {
            return
          }
          if (groupKey === KEYWORDS) {
            const keywords = _.split(filterValues, '|')
            formattedSearch[KEYWORDS] = keywords?.length > 0 ? keywords : null
            return
          }
          if (NOT_GROUP_FILTER.includes(groupKey)) {
            formattedSearch[groupKey] = filterValues
            return
          }
          const group = this.wrappedFilterGroupList[groupKey]
          if (!group) {
            return
          }
          if (!search[groupKey]) {
            search[groupKey] = {}
          }
          const searchFilterValues = search[groupKey]
          const filterList = group.filterList
          _.forEach(filterValues, (value, filterName) => {
            if (filterName === '_exclude' || _.endsWith(filterName, '_isUseAiSearch')) {
              searchFilterValues[filterName] = value
              return
            }
            if (this.isNullOrUndefinedOrEmpty(value)) {
              return
            }
            const filter = filterList[filterName]
            if (!filter) {
              return
            }
            searchFilterValues[filterName] = value

            if (filter.remoteLoadSelect) {
              const option = filter.option
              if (!option.hasLoad && !option.loading) {
                option.loading = true
                promiseList.push(Promise.resolve(option.loader()).then((dataList) => {
                  if (filter.loadMore) {
                    option.loadedData = dataList
                  } else {
                    option.data = dataList
                    option.loadedData = dataList
                  }
                  option.hasLoad = true
                }).catch((e) => {
                  option.hasLoad = false
                  // eslint-disable-next-line no-console
                  console.error(e)
                  return e
                }).finally(() => {
                  option.loading = false
                }))
              }
            }
          })
        })
        await Promise.all(promiseList)
        const loadDataPromiseList = []
        _.forEach(search, (filterValues, groupKey) => {
          const group = this.wrappedFilterGroupList[groupKey]
          const filterList = group.filterList
          const formattedFilterValues = formattedSearch[groupKey] = {}
          const excludeFilterNames = filterValues._exclude
          if (excludeFilterNames) {
            formattedFilterValues._exclude = excludeFilterNames
          }
          _.forEach(filterValues, (valueList, filterName) => {
            const filter = filterList[filterName]
            if (_.endsWith(filterName, '_isUseAiSearch')) {
              formattedFilterValues[filterName] = valueList
            }
            if (!filter) {
              return
            }
            if (filter.loadTemplate) {
              loadDataPromiseList.push(Promise.resolve(filter.loadTemplate(valueList, filter, formattedFilterValues)))
            } else if (filter.isSelect) {
              const valueKey = filter.valueKey
              const option = filter.option
              let values = []
              const dataList = option.loadedData
              if (!dataList) {
                values = valueList
              } else if (filter.option.grouped) {
                _.forEach(dataList, groupedData => {
                  _.forEach(groupedData[filter.option.groupOptionsDataKey], data => {
                    const dataValue = valueKey ? data[valueKey] : data
                    if (valueList.includes(dataValue)) {
                      values.push(data)
                    }
                  })
                })
              } else {
                const selectedDataList = []
                values = _.filter(dataList, data => {
                  const dataValue = valueKey ? data[valueKey] : data
                  if (_.includes(valueList, dataValue)) {
                    selectedDataList.push(data)
                    return true
                  }
                  return false
                })
                if (filter.loadMore) {
                  option.data = selectedDataList
                }
              }
              formattedFilterValues[filterName] = filter.multiple ? values : values[0]
            } else if (filter.elType === 'el-input') {
              formattedFilterValues[filterName] = valueList
            } else {
              formattedFilterValues[filterName] = filter.multiple ? valueList : valueList[0]
            }
          })
        })
        await Promise.all(loadDataPromiseList)
        this.search = formattedSearch
      } else {
        this.search = {}
        this.resetAiSearch()
      }
      return this.formatSearchKeyword()
    },
    handleSearchSelectPaste (e, searchKey) {
      e.preventDefault()
      const text = e.clipboardData.getData('text')
      const parseValue = _.chain(text)
        .replace('\r', '\n')
        .split('\n')
        .map(v => _.trim(v))
        .filter()
        .value()
      const oldValue = _.get(this.search, searchKey)
      const value = _.chain(oldValue).concat(parseValue).filter().value()
      this.$set(this.search, searchKey, value)
    },
    resetAiSearch () {
      this.aiSearch = null
      this.aiSearchResult = null
      this.aiSearchDisplayValue = null
      this.$refs.aiSearchCollapseInfo.resetAiSearch()
      this.$refs.aiSearchCollapseInfo.closeAiSearch()
    },
    async setAiSearchParams (searchInfo) {
      if (searchInfo) {
        const search = {}
        const promiseList = []
        const formattedSearch = {}
        _.forEach(searchInfo, (filterValues, groupKey) => {
          if (this.isNullOrUndefinedOrEmpty(filterValues)) {
            return
          }
          if (_.includes(AI_EXTRA_PARAMS, groupKey)) {
            formattedSearch[groupKey] = filterValues
            return
          }
          if (groupKey === KEYWORDS) {
            const keywords = _.split(filterValues, '|')
            formattedSearch[KEYWORDS] = keywords?.length > 0 ? keywords : null
            return
          }
          if (NOT_GROUP_FILTER.includes(groupKey)) {
            formattedSearch[groupKey] = filterValues
            return
          }
          const group = this.wrappedFilterGroupList[groupKey]
          if (!group) {
            return
          }
          if (!search[groupKey]) {
            search[groupKey] = {}
          }
          const searchFilterValues = search[groupKey]
          const filterList = group.filterList
          _.forEach(filterValues, (value, filterName) => {
            if (filterName === '_exclude' || _.endsWith(filterName, '_isUseAiSearch')) {
              searchFilterValues[filterName] = value
              return
            }
            if (this.isNullOrUndefinedOrEmpty(value)) {
              return
            }
            const filter = filterList[filterName]
            if (!filter) {
              return
            }
            searchFilterValues[filterName] = value

            if (filter.remoteLoadSelect) {
              const option = filter.option
              if (!option.hasLoad && !option.loading) {
                option.loading = true
                promiseList.push(Promise.resolve(option.loader()).then((dataList) => {
                  if (filter.loadMore) {
                    option.loadedData = dataList
                  } else {
                    option.data = dataList
                    option.loadedData = dataList
                  }
                  option.hasLoad = true
                }).catch((e) => {
                  option.hasLoad = false
                  // eslint-disable-next-line no-console
                  console.error(e)
                  return e
                }).finally(() => {
                  option.loading = false
                }))
              }
            }
          })
        })
        await Promise.all(promiseList)
        const loadDataPromiseList = []
        _.forEach(search, (filterValues, groupKey) => {
          const group = this.wrappedFilterGroupList[groupKey]
          const filterList = group.filterList
          const formattedFilterValues = formattedSearch[groupKey] = {}
          const excludeFilterNames = filterValues._exclude
          if (excludeFilterNames) {
            formattedFilterValues._exclude = excludeFilterNames
          }
          _.forEach(filterValues, (valueList, filterName) => {
            const filter = filterList[filterName]
            if (_.endsWith(filterName, '_isUseAiSearch')) {
              formattedFilterValues[filterName] = valueList
            }
            if (!filter) {
              return
            }
            if (filter.loadTemplate) {
              _.forEach(valueList, value => {
                loadDataPromiseList.push(
                  Promise.resolve(filter.filterMethod(value, filter).then(options => {
                    filter.option.data = options
                    formattedFilterValues[filterName] = options
                  }))
                )
              })
            } else if (filter.isSelect) {
              const option = filter.option
              let values
              const dataList = option.loadedData
              if (!dataList) {
                values = valueList
              } else {
                if (filter.option.grouped) {
                  values = []
                  _.forEach(dataList, (groupedData) => {
                    _.forEach(groupedData[filter.option.groupOptionsDataKey], (data, index) => {
                      let isMatch = false
                      _.forEach(valueList, value => {
                        let filterMethod
                        if (filter.filterMethod) {
                          filterMethod = filter.filterMethod
                        } else {
                          filterMethod = this.defaultFilterMethod
                        }
                        const isFind = filterMethod(value, filter, data, index, dataList)
                        if (isFind) {
                          isMatch = true
                          return false
                        }
                      })
                      if (isMatch) {
                        values.push(data)
                      }
                    })
                  })
                } else {
                  const selectedDataList = []
                  values = _.filter(dataList, (data, index) => {
                    let isMatch = false
                    _.forEach(valueList, value => {
                      let filterMethod
                      if (filter.filterMethod) {
                        filterMethod = filter.filterMethod
                      } else {
                        filterMethod = this.defaultFilterMethod
                      }
                      const isFind = filterMethod(value, filter, data, index, dataList)
                      if (isFind) {
                        isMatch = true
                        selectedDataList.push(data)
                        return false
                      }
                    })
                    return isMatch
                  })
                  if (filter.loadMore) {
                    option.data = selectedDataList
                  }
                }
              }
              formattedFilterValues[filterName] = filter.multiple ? values : values[0]
            } else if (filter.elType === 'el-input') {
              formattedFilterValues[filterName] = valueList
            } else {
              formattedFilterValues[filterName] = filter.multiple ? valueList : valueList[0]
            }
          })
        })
        await Promise.all(loadDataPromiseList)
        this.search = formattedSearch
      } else {
        this.search = {}
      }
      return this.formatSearchKeyword()
    },
    defaultFilterMethod (queryValue, filter, option, index, optionList) {
      queryValue = _.toLower(queryValue)
      let searchTextList
      if (filter.searchKey) {
        searchTextList = option[filter.searchKey]
      } else {
        searchTextList = option.searchTextList
      }
      if (!searchTextList) {
        const label = _.get(option, filter.option.labelKey || 'label')
        if (!label) {
          return
        }
        searchTextList = [label]
      }
      if (!_.isArray(searchTextList)) {
        searchTextList = [searchTextList]
      }
      return _.chain(searchTextList)
        .map(_.toLower)
        .some(text => text === queryValue)
        .value()
    },
    formatSearchKeyword () {
      const search = { ...this.search }
      if (search.keywords) {
        search.keywords = _.join(this.search.keywords, '|') || null
      }
      return search
    },
    onSearchTemplateChange (template) {
      this.$emit('update:searchTemplate', template)
      this.resetSearch(template)
    },
    handleSearchSaveBtnClick () {
      this.$refs.saveSearchDialog.openDialog()
    },
    handleSaveSearchSave (templateInfo, callback) {
      const search = this.dumpSearch()
      if (templateInfo.isEditMode) {
        const template = _.find(this.searchTemplateList, item => item.name === templateInfo.templateName)
        this.$emit('updateSearchTemplate', { ...template, search }, (savedTemplate) => {
          template.search = savedTemplate.search
          callback(savedTemplate)
        })
      } else {
        this.$emit('createSearchTemplate', {
          name: templateInfo.templateName,
          search
        }, (savedTemplate) => {
          this.searchTemplateList.push(savedTemplate)
          this.$emit('update:searchTemplate', savedTemplate)
          callback(savedTemplate)
        })
      }
    },
    handleSearchResetBtnClick () {
      this.resetSearch()
      this.$emit('clearSearchTemplate')
    },
    handleActionBtnClick (actionItem) {
      actionItem.handler && actionItem.handler()
    },
    handleAddBtnClick (actionItem) {
      if (this.noAction) {
        return
      }
      actionItem.handler()
    },
    deleteSearchTemplate (searchTemplate, index) {
      this.$refs.confirmDeleteDialog.openDialog()
      this.deletedTemplate = searchTemplate
      this.deletedTemplateIndex = index
    },
    handleConfirmDelete () {
      this.$emit('deleteSearchTemplate', this.deletedTemplate, this.deletedTemplateIndex)
    },
    onListTypeBtnClick () {
      this.$emit('selectAll', false)
      this.listType = this.listType === 'table' ? 'card' : 'table'
    },
    onPotentialBtnClick () {
      const params = this.$route.params
      const jobId = params.jobId
      const talentFilterGroupId = params.talentFilterGroupId
      if (this.isPotential) {
        const url = this.fromJobPotential ? `/v-admin/job/${jobId}/application` : `/v-admin/talent-pool/${talentFilterGroupId}/`
        this.$router.push(url)
      } else {
        const url = this.fromJobPotential ? `/v-admin/job/${jobId}/potential/list` : `/v-admin/talent-pool/${talentFilterGroupId}/potential/list`
        this.$router.push(url)
      }
    },
    handleCandidateIdChange (value) {
      if (value.length < 1) {
        return
      }
      const newValue = _.chain(value).last().split(/[\r\n,;，； ]+/).filter().value()
      value.splice(value.length - 1, 1, ...newValue)
    },
    handleEmployeeIdChange (value) {
      if (value.length < 1) {
        return
      }
      const newValue = _.chain(value).last().split(/[\r\n,;，； ]+/).filter().value()
      value.splice(value.length - 1, 1, ...newValue)
    },
    handleOnlyMyCandidateChange (status) {
      const applicationDetailRef = _.find(this.$refs.filterGroup, (filterRef) => filterRef.$vnode.key === FILTER_GROUP_TYPE.APPLICATION_DETAIL)
      if (!applicationDetailRef) {
        return
      }
      const jobSelectorRef = applicationDetailRef.$refs.jobSelector
      if (status) {
        jobSelectorRef.selectMyJob()
      } else {
        jobSelectorRef.clearSelect()
      }
    },
    handleCandidateIdClear () {
      this.search.candidateId = null
    },
    handleEmployeeIdClear () {
      this.search.employeeId = null
    },
    isNullOrUndefinedOrEmpty (value) {
      if (_.isNil(value)) {
        return true
      }
      if (_.isArray(value)) {
        return _.isEmpty(value)
      }
      if (_.isNumber(value)) {
        return false
      }
      return !value
    },
    expandChange (expand, key) {
      this.$emit('expandChange', expand, key)
    },
    handleOpen () {
      this.showCollapse = true
      this.disableAISearch = true
    },
    handleClose () {
      this.showCollapse = false
      this.disableAISearch = false
    },
    handleAISearch () {
      const aiSearchRef = this.$refs.aiSearchCollapseInfo
      if (!aiSearchRef) {
        return
      }
      this.disableAISearch = true
      aiSearchRef.openCollapse()
      aiSearchRef.handleAISearch(this.aiSearch)
    },
    formatAiSearchParams (aiSearchParams) {
      const formatAiSearchResult = {}
      aiSearchParams.work_year = [this.formatWorkYear(aiSearchParams.work_year_min), this.formatWorkYear(aiSearchParams.work_year_max)]
      aiSearchParams.location = _.map(aiSearchParams.location, location => {
        return _.map(location, value => value)
      })
      _.forEach(aiSearchParams, (value, key) => {
        if (key === 'company_name') {
          if (this.search[FILTER_GROUP_TYPE.EMPLOYER]?.workCompanyName?.isUseAiSearch) {
            formatAiSearchResult[FILTER_GROUP_TYPE.EMPLOYER] = {
              workCompanyName: {
                ...this.search[FILTER_GROUP_TYPE.EMPLOYER].workCompanyName,
                name: _.isArray(value) ? value : [value]
              }
            }
          }
        } else if (key === 'job_function') {
          formatAiSearchResult[FILTER_GROUP_TYPE.INDUSTRY] = {
            metaJobFunction: _.isArray(value) ? value : [value],
            ...formatAiSearchResult[FILTER_GROUP_TYPE.INDUSTRY]
          }
        } else if (key === 'industry') {
          formatAiSearchResult[FILTER_GROUP_TYPE.INDUSTRY] = {
            metaIndustry: _.isArray(value) ? value : [value],
            ...formatAiSearchResult[FILTER_GROUP_TYPE.INDUSTRY]
          }
        } else if (key === 'recent_industry') {
          // formatAiSearchResult[FILTER_GROUP_TYPE.INDUSTRY] = {
          //   lastWorkIndustry: _.isArray(value) ? value : [value],
          //   ...formatAiSearchResult[FILTER_GROUP_TYPE.INDUSTRY]
          // }
        }
        const mapFilterName = AI_SEARCH_PARAMS_MAPPING[key]
        if (_.includes(AI_EXTRA_PARAMS, mapFilterName)) {
          formatAiSearchResult[mapFilterName] = value
          return
        }
        if (this.isNullOrUndefinedOrEmpty(value)) {
          return
        }
        if (!_.isArray(value)) {
          value = [value]
        }
        let isMatch = false
        _.forEach(this.wrappedFilterGroupList, (filterGroup, filterGroupKey) => {
          if (!_.has(formatAiSearchResult, filterGroupKey)) {
            formatAiSearchResult[filterGroupKey] = {}
          }
          if (isMatch) {
            return false
          }
          _.forEach(filterGroup.filterList, (filter, filterName) => {
            if (mapFilterName === filterName) {
              isMatch = true
              formatAiSearchResult[filterGroupKey][mapFilterName] = value
              return false
            }
          })
        })
      })
      return formatAiSearchResult
    },
    formatWorkYear (workYear) {
      if (!workYear) {
        return null
      }
      const num = Number(workYear.trim())
      if (isNaN(num)) {
        return null
      }
      return Math.floor(num)
    },
    handleMessage (event) {
      if (event.origin !== window.location.origin) {
        if (process.env.VUE_APP_ENV === 'development') {
        } else {
          return
        }
      }

      const { name, params } = event.data
      if (name === 'sendApplicationInfo') {
        this.aiSearchResult = JSON.parse(params)
        if (process.env.NODE_APP_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.log('aiSearchResult', this.aiSearchResult)
        }
        if (this.aiSearchResult.search) {
          const aiSearchParams = this.formatAiSearchParams(this.aiSearchResult.params)
          this.setAiSearchParams(aiSearchParams)
        }
        this.aiSearch = this.aiSearchResult.summary
        this.aiSearchDisplayValue = this.aiSearchResult.summary
      }
    }
  },
  watch: {
    filterGroupList: {
      immediate: true,
      handler () {
        this.wrapFilterGroupList()
      }
    },
    search: {
      deep: true,
      handler () {
        if (this.wrappingFilterGroupList) {
          return
        }
        if (!this.doSearch) {
          this.doSearch = _.debounce(() => {
            this.$emit('scrollToTop', this.listType)
            this.$emit('search', this.formatSearchKeyword())
          }, 1000)
        }
        if (this.noTriggerSearch) {
          return
        }
        this.doSearch()
      }
    },
    currentGroupFilter: {
      handler () {
        this.initTalentFilterReportChart()
      }
    },
    currentSelectedCount: {
      handler () {
        if (this.currentSelectedCount === 0) {
          this.checkAllStatus = false
        } else {
          this.checkAllStatus = true
        }
      }
    },
    onlyMyCandidate: {
      handler () {
        this.$emit('onlyMyCandidateChange', this.onlyMyCandidate)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$bg-color: #f7f8fa;
$border-color: #eef2f4;

.filter-list-container {
  display: flex;
  height: 100%;

  .aside-filter-container {
    margin-left: -20px;

    &.shrink {
      margin-left: -20px;
    }

    .container-wrapper {
      width: 292px;
      height: 100%;
      padding: 20px;
      border: 1px solid $border-color;
      border-radius: 6px;
      background: $bg-color;
      overflow: auto;
    }

    .talent-filter-group-content {
      .talent-filter-group-name {
        color: #303133;
        font-size: 14px;
        word-wrap: break-word;
        white-space: normal;
        font-weight: bold;
      }

      .talent-filter-group-chart {
        margin-top: 6px;
        display: flex;
        align-items: center;
        .talent-filter-report {
          width: 100px;
          height: 100px;
        }

        .talent-filter-group-count {
          color: #0096CC;
          font-size: 18px;
          margin-left: 16px;
          font-weight: bold;
        }
      }
    }

    .ai-search-container {
      .container-label {
        margin-bottom: 10px;
        color: #606266;
        font-size: 16px;
        font-weight: bold;
      }

      .container-content {
        border: 1px solid #c0c4cc;
        border-radius: 4px;
        padding: 10px;
        background-color: #fff;

        .ai-search-content {
          .el-textarea {
            ::v-deep {
              .el-textarea__inner {
                border-width: 0;
                scrollbar-width: none;
              }

              &.is-disabled {
                .el-textarea__inner {
                  background-color: #f2f3f4;
                }
              }
            }
          }
        }

        .ai-search-footer {
          height: 24px;
          text-align: right;

          .search-icon {
            font-size: 24px;
            color: #0096cc;
            cursor: pointer;

            &:hover {
              color: #0096cc;
            }
          }
        }
      }

      &.is-disabled {
        .container-content {
          background-color: #f2f3f4;
          cursor: not-allowed;

          .ai-search-footer {
            .search-icon {
              font-size: 24px;
              color: #c0c4cc;
              cursor: not-allowed;
            }
          }
        }
      }
    }

    .keywords-filter-container {
      margin-top: 10px;
      padding-bottom: 14px;
      border-bottom: 1px solid #e7eaeb;
    }

    .candidate-name-filter-container {
      margin-top: 10px;
      padding-bottom: 14px;
      border-bottom: 1px solid #e7eaeb;

      &.has-candidate-id-filter {
        padding-bottom: 0;
        border-bottom: 0;
      }
    }

    .candidate-id-filter-container {
      margin-top: 10px;
      padding-bottom: 14px;
      border-bottom: 1px solid #e7eaeb;

      &.has-employee-id-filter {
        padding-bottom: 0;
        border-bottom: 0;
      }

      &.has-my-candidate-switch {
        padding-bottom: 0;
        border-bottom: 0;
      }
    }

    .employee-id-filter-container {
      margin-top: 10px;
      padding-bottom: 14px;
      border-bottom: 1px solid #e7eaeb;

      &.has-my-candidate-switch {
        padding-bottom: 0;
        border-bottom: 0;
      }
    }

    .my-candidate-switch-container {
      margin-top: 10px;
      padding-bottom: 14px;
      border-bottom: 1px solid #e7eaeb;

      .my-candidate {
        width: 100%;
        justify-content: space-between;

        ::v-deep {
          .el-switch__label {
            color: #606266;

            &.is-active {
              color: #606266;
            }
          }
        }
      }
    }

    .search-template-container {
      margin-top: 16px;
      padding-bottom: 14px;
      border-bottom: 1px solid #e7eaeb;

      .container-label {
        margin-bottom: 10px;
        color: #606266;
        font-size: 16px;
        font-weight: bold;
      }
    }

    .filter-group-list {

      .filter-group {
        border-bottom: 1px solid #e7eaeb;
      }
    }
  }

  .main-container {
    flex: 1;
    display: flex;
    overflow: hidden;

    &.ai-collapse {
      margin-right: -18px;

      .container-content {
        margin-right: 13px;
      }
    }

    .container-content {
      flex: 1;
      display: flex;
      overflow: auto;

      .content-wrapper {
        flex: 1;
        display: flex;
        flex-direction: column;
        overflow: auto;
      }
    }

    .list-container {
      flex: 1;
      display: flex;
      margin-left: 13px;
      padding: 20px 14px 32px 14px;
      border-radius: 6px;
      background: #ffffff;
      flex-direction: column;
      overflow: hidden;

      .job-name {
        font-size: 18px;
        height: 25px;
        line-height: 25px;
        font-weight: 500;
        margin-bottom: 20px;
      }

      .list-head {
        display: flex;

        .select-all {
          line-height: 32px;
          margin-right: 14px;

          ::v-deep .el-checkbox__inner {
            width: 18px;
            height: 18px;

            &::after {
              height: 9px;
              left: 6px;
              top: 2px;
            }
          }
        }

        .search-param-container {
          flex: 1;
          display: flex;
          align-items: baseline;

          .candidate-count-container {
            margin-bottom: 14px;

            .candidate-count {
              display: inline-block;
              margin-right: 14px;
              padding: 8px 15px;
              border: 1px solid #dddedf;
              border-radius: 4px 4px 0 0;
              vertical-align: middle;
              line-height: 1;
            }
          }

          .search-param-list-wrapper {
            overflow: hidden;
            padding-bottom: 4px;
          }

          .search-param-list {
            display: flex;
            max-height: 78px;
            flex-wrap: wrap;
            overflow: hidden;

            .search-param {
              margin-right: 14px;
              margin-bottom: 14px;
              padding: 8px 15px;
              border-radius: 18px;
              border: 1px solid #dddedf;
              line-height: 10px;

              .param-summary {
                vertical-align: middle;
                line-height: 1;
              }

              .cancel-btn {
                display: inline-block;
                vertical-align: middle;
                cursor: pointer;

                .btn-icon {
                  font-size: 12px;
                  margin-left: 10px;
                }
              }

              &.ai-search {
                --borderWidth: 2px;
                position: relative;
                z-index: 0;
                overflow: hidden;

                &::before {
                  content: '';
                  position: absolute;
                  z-index: -2;
                  left: calc(-100px + 50%);
                  top: calc(-100px + 50%);
                  width: 200px;
                  height: 200px;
                  background-color: #fff;
                  background-repeat: no-repeat;
                  background-position: 0 0;
                  background-image: conic-gradient(
                      #fff,#0096cc 10deg 170deg,
                      #fff,
                      #0096cc 190deg 350deg,
                      #fff
                  );
                  animation: rotate 4s linear infinite;
                }

                &::after {
                  content: '';
                  position: absolute;
                  z-index: -1;
                  left: calc(var(--borderWidth) / 2);
                  top: calc(var(--borderWidth) / 2);
                  width: calc(100% - var(--borderWidth));
                  height: calc(100% - var(--borderWidth));
                  background: #fff;
                  border-radius: 18px;
                }
              }
            }
          }

          .search-param-list:hover {
            overflow: auto;
          }

          .operate-btn-group {
            word-break: keep-all;
            white-space: nowrap;

            .operate-btn {
              display: inline-block;
              vertical-align: middle;
              cursor: pointer;

              .btn-icon {
                display: inline-block;
                margin-top: 2px;
                font-size: 18px;
                color: #0096cc;
              }
            }

            .save-btn {
              margin-left: 8px;
            }

            .reset-btn {
              margin-left: 18px;
            }
          }
        }

        .head-btn-group {
          margin-left: 16px;
          margin-right: 0;

          .head-btn {
            display: inline-block;
            vertical-align: middle;

            + .head-btn {
              margin-left: 24px;
            }
          }

          .action-dropdown {
            .dropdown-btn {
              padding: 8px 20px;
              font-size: 16px;
              border-color: #0096cc;
              color: #0096cc;

              .dropdown-btn-icon {
                margin-left: 5px;
                transform: rotate(90deg);
              }

              .ai-iconfont {
                margin-left: 5px;
              }

              &.is-disabled {
                color: #c0c4cc;
                border-color: #ebeef5
              }
            }
          }

          .action-menu {
            transform-origin: center top;
            position: absolute;
            z-index: 2;
            width: auto;
            padding: 10px 0;
            box-shadow: 0px 2px 12px 0px rgba(0, 0, 0, 0.1);

            .action-submenu {
              line-height: 36px;
              height: 36px;
              color: #606266;

              &:hover {
                color: #66b1ff;
              }

              ::v-deep .el-submenu__title {
                line-height: 36px;
                height: 36px;
                color: #606266;
              }
            }
          }

          .action-menu {
            transform-origin: center top;
            position: absolute;
            z-index: 2;
            width: auto;
            box-shadow: 0px 2px 12px 0px rgba(0, 0, 0, 0.1);

            .action-submenu :hover {
              color: #66b1ff;
            }
          }

          .add-btn {
            padding: 0;
            border: 0;
            border-radius: 4px;

            .btn-icon {
              font-size: 32px;
              color: #0096cc;
            }

            &.is-disabled {
              .btn-icon {
                color: #c0c4cc;
              }
            }
          }

          .potential-btn {
            cursor: pointer;

            .btn-icon {
              font-size: 32px;
              color: #0096cc;
            }
          }

          .list-type-button {
            cursor: pointer;

            .btn-icon {
              font-size: 28px;
            }
          }
        }
      }

      .list-wrapper {
        position: relative;
        flex: 1;
        overflow: hidden;

        .loading {
          position: absolute;
          top: 0;
          left: 0;
        }
      }
    }
  }

}

.search-template-dropdown {
  .option {
    display: flex;
    justify-content: space-between;

    .delete-icon {
      line-height: inherit;
      margin-left: 5px;
    }
  }
}

.action-children-container {
  padding: 10px 0;

  .child-item {
    line-height: 36px;
    padding: 0 10px;
    margin: 0;
    cursor: pointer;

    &:hover {
      color: #0096cc;
      background-color: #ecf5ff;
    }
  }
}

.el-menu--vertical {
  .action-submenu-child {
    height: 36px;
    line-height: 36px;
    color: #606266;

    &:hover {
      color: #0096cc;
    }
  }
}

.no-arrow-dropdown-menu {
  .disabled-dropdown-item {
    cursor: not-allowed;
  }
}

</style>
